import { useState } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useAppContext } from "../../AppContext";
import { Perm } from "../../permissions/permissions";
import { usePermissions } from "../../permissions/usePermissions";
import { getJwt, HttpError } from "@/api/common";

const BASE_URL = import.meta.env.VITE_API_URL as string;

const ORACLE_CERTIFICATION_CALLS = [
    { name: "OpenAuthorize", value: 1 },
    { name: "SignIn", value: 2 },
    { name: "GetToken", value: 3 },
    { name: "GetOrganizations", value: 4 },
    { name: "GetOrganization", value: 5 },
    { name: "GetLocationsForOrganization", value: 6 },
    { name: "GetLocation", value: 7 },
    { name: "GetRevenueCentersForLocation", value: 8 },
    { name: "GetRevenueCenter", value: 9 },
    { name: "SearchLocations", value: 10 },
    { name: "SearchRevenueCenters", value: 11 },
    { name: "GetMenuSummary", value: 12 },
    { name: "GetMenuV2", value: 13 },
    { name: "GetMenuV1", value: 14 },
    { name: "GetUnavailableMenuItems", value: 15 },
    { name: "GetTenderCollection", value: 16 },
    { name: "GetTaxDefinitions", value: 17 },
    { name: "GetDiscountCollection", value: 18 },
    { name: "GetServiceChargeCollection", value: 19 },
    { name: "GetEmployeeByEmployeeId", value: 20 },
    { name: "GetBarcodeCollection", value: 21 },
    { name: "CalculatorPreview", value: 22 },
    { name: "HeadConnectionStatus", value: 23 },
    { name: "ListChecks", value: 24 },
    { name: "ListChecksByTable", value: 25 },
    { name: "GetCheckByCheckRef", value: 26 },
    { name: "CreateCheck", value: 27 },
    { name: "AddRoundItem", value: 28 },
    { name: "AddRoundDiscount", value: 29 },
    { name: "AddRoundFirstPayment", value: 30 },
    { name: "AddRoundSecondPayment", value: 31 },
] as const;

type OracleCertificationCall = (typeof ORACLE_CERTIFICATION_CALLS)[number]["value"];

type OracleCertificationResult = {
    success?: boolean;
    name?: string;
    method?: string;
    requestUri?: string;
    requestBody?: string;
    requestHeaders?: string[];
    statusCode?: number;
    timestamp?: string;
    responseBody?: string;
};

// `json` rather than `application/json`: PortalAPI answers errors with RFC 7807, served as
// `application/problem+json`, which the narrower test misses — so the body came back as text and the
// explanation inside it was shown to the user wrapped in raw JSON (TTD-143).
const readBody = async (response: Response): Promise<unknown> => {
    const contentType = response.headers.get("content-type") ?? "";
    return contentType.includes("json")
        ? await response.json().catch(() => null)
        : await response.text().catch(() => "");
};

const getJwtSub = (jwt: string | null | undefined): string | null => {
    if (!jwt) return null;

    try {
        const payload = jwt.split(".")[1];
        if (!payload) return null;

        const paddedPayload = payload.padEnd(payload.length + (4 - payload.length % 4) % 4, "=");
        const normalizedPayload = paddedPayload.replace(/-/g, "+").replace(/_/g, "/");
        const claims = JSON.parse(atob(normalizedPayload)) as { sub?: string };
        return claims.sub ?? null;
    } catch {
        return null;
    }
};

const formatError = (error: unknown) => {
    if (error instanceof HttpError) {
        const body = typeof error.body === "string" ? error.body : JSON.stringify(error.body);
        return body && body !== "null" ? `HTTP ${error.status}: ${body}` : `HTTP ${error.status}`;
    }

    return error instanceof Error ? error.message : "Request failed.";
};

const isOracleCertificationResult = (value: unknown): value is OracleCertificationResult =>
    !!value && typeof value === "object" && ("requestUri" in value || "responseBody" in value || "statusCode" in value);

const formatBody = (body: string | undefined) => {
    if (!body) return "";

    try {
        return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
        return body;
    }
};

const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return timestamp;

    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "medium",
    }).format(date);
};

const formatList = (items: string[]) => {
    if (items.length <= 1) return items[0] ?? "";
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const formatResultText = (result: OracleCertificationResult) => {
    const requestBody = formatBody(result.requestBody);
    const responseBody = formatBody(result.responseBody);
    const lines = [
        result.name ?? "Oracle Certification Call",
        "",
        result.timestamp ? `Timestamp: ${formatTimestamp(result.timestamp)}` : "",
        result.statusCode ? `Status: ${result.statusCode}` : "",
        "",
        `${result.method ?? ""} ${result.requestUri ?? ""}`.trim(),
    ].filter((line, index) => line || index === 1 || index > 3);

    if (result.requestHeaders?.length) {
        lines.push("", "Request Headers", result.requestHeaders.join("\n"));
    }

    if (requestBody) {
        lines.push("", "Request Body", requestBody);
    }

    lines.push("", "Response Body", responseBody || "No response body.");

    return lines.join("\n");
};

const downloadTextFile = (fileName: string, text: string) => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

const BodyText = ({ children }: { children: string }) => (
    <Typography
        component="pre"
        sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            bgcolor: "background.default",
            border: "1px solid",
            borderColor: "divider",
            color: "text.primary",
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
            fontSize: 13,
            lineHeight: 1.5,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
        }}
    >
        {children}
    </Typography>
);

const ResponseDetails = ({ result }: { result: OracleCertificationResult }) => {
    const requestBody = formatBody(result.requestBody);
    const responseBody = formatBody(result.responseBody);
    const timestamp = formatTimestamp(result.timestamp);

    return (
        <Stack spacing={1.5}>
            <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {result.name ?? "Oracle Certification Call"}
                </Typography>
                {(timestamp || result.statusCode) ? (
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="baseline"
                        justifyContent="space-between"
                        sx={{ gap: 1, flexWrap: "wrap" }}
                    >
                        {timestamp ? (
                            <Typography variant="body2" color="text.secondary">
                                {timestamp}
                            </Typography>
                        ) : null}
                        {result.statusCode ? (
                            <Typography
                                variant="body2"
                                color={result.success ? "success.main" : "error.main"}
                                sx={{ ml: "auto" }}
                            >
                                Status: {result.statusCode}
                            </Typography>
                        ) : null}
                    </Stack>
                ) : null}
            </Box>

            <BodyText>{`${result.method ?? ""} ${result.requestUri ?? ""}`.trim()}</BodyText>

            {result.requestHeaders?.length ? (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Request Headers</Typography>
                    <BodyText>{result.requestHeaders.join("\n")}</BodyText>
                </Box>
            ) : null}

            {requestBody ? (
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Request Body</Typography>
                    <BodyText>{requestBody}</BodyText>
                </Box>
            ) : null}

            <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Response Body</Typography>
                <BodyText>{responseBody || "No response body."}</BodyText>
            </Box>
        </Stack>
    );
};

const OracleCertification = () => {
    const app = useAppContext();
    // Running a certification call POSTs to a live location, so it answers to update rather than read. Read
    // access alone gets the screen and the request preview, which is what it is useful for on its own.
    const canRun = usePermissions().canUpdate(Perm.adminConfiguration);
    const [selectedCall, setSelectedCall] = useState<OracleCertificationCall>(4);
    const [validationError, setValidationError] = useState("");

    const { mutateAsync: runCertificationCall, isPending, data, error, reset } = useMutation({
        mutationFn: async (call: OracleCertificationCall) => {
            const jwt = getJwt();
            const sub = getJwtSub(jwt);
            const url = new URL(`${BASE_URL}/restaurant/oraclecertification/${call}`);

            if (sub) url.searchParams.set("sub", sub);

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${jwt}`,
                    "x-location-id": app.locationId!,
                },
            });
            const body = await readBody(response);

            if (!response.ok) throw new HttpError(response.status, body);
            return body;
        },
    });

    const handleChange = (event: SelectChangeEvent<OracleCertificationCall>) => {
        setSelectedCall(Number(event.target.value) as OracleCertificationCall);
        setValidationError("");
        reset();
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        const missingScope = [
            !app.organizationId ? "organization" : "",
            !app.brandId ? "brand" : "",
            !app.locationId ? "location" : "",
        ].filter(Boolean);

        if (missingScope.length) {
            setValidationError(`Select ${formatList(missingScope)} before running the certification call.`);
            return;
        }

        setValidationError("");
        await runCertificationCall(selectedCall);
    };

    const selectedOption = ORACLE_CERTIFICATION_CALLS.find((call) => call.value === selectedCall);
    const selectedUrl = new URL(`${BASE_URL}/restaurant/oraclecertification/${selectedOption?.value ?? selectedCall}`);
    const sub = getJwtSub(getJwt());
    const fallbackResponseText = typeof data === "string" ? data : data === undefined ? "" : JSON.stringify(data, null, 2);

    if (sub) selectedUrl.searchParams.set("sub", sub);

    const handleExport = () => {
        const text = isOracleCertificationResult(data)
            ? formatResultText(data)
            : fallbackResponseText || "Request completed.";
        const callName = selectedOption?.name ?? "OracleCertification";

        downloadTextFile(`${callName}.txt`, text);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Paper
                component="form"
                variant="outlined"
                onSubmit={handleSubmit}
                sx={{ p: 2, maxWidth: 680 }}
            >
                <Stack spacing={2}>
                    <Typography variant="h6">Oracle Certification</Typography>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="oracle-certification-call-label">Certification Call</InputLabel>
                            <Select
                                labelId="oracle-certification-call-label"
                                id="oracle-certification-call"
                                label="Certification Call"
                                value={selectedCall}
                                onChange={handleChange}
                            >
                                {ORACLE_CERTIFICATION_CALLS.map((call) => (
                                    <MenuItem key={call.name} value={call.value}>
                                        {call.name} ({call.value})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {canRun &&
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            disabled={isPending}
                            sx={{ flexShrink: 0 }}
                        >
                            {isPending ? "Running" : "Run"}
                        </Button>
                        }
                    </Stack>

                    <Typography variant="body2" color="text.secondary">
                        POST {selectedUrl.toString()}
                    </Typography>

                    {error ? <Alert severity="error">{formatError(error)}</Alert> : null}
                    {validationError ? <Alert severity="warning">{validationError}</Alert> : null}
                    {isOracleCertificationResult(data) ? <ResponseDetails result={data} /> : null}
                    {data !== undefined && !isOracleCertificationResult(data) ? (
                        <Alert severity="success">{fallbackResponseText || "Request completed."}</Alert>
                    ) : null}

                    {data !== undefined ? (
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}>
                                Export Text
                            </Button>
                        </Box>
                    ) : null}
                </Stack>
            </Paper>
        </Box>
    );
};

export default OracleCertification;
